import React, {CSSProperties, useEffect, useState} from 'react';
import {useNavigate} from 'react-router';

import Label from '@components/inputs/text/Label';
import DatePicker from '@components/inputs/DateInput';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Button from '@components/buttons/Button/Button';

import { CancellationReasonState, CancellationReasonAction } from './cancellationReasonReducer';
import cancellationReasons from "./cancellationReasons";


// Enum definition in TypeScript
enum CompletionStatus {
    Category = "Category",
    Subcategory = "Subcategory",
    Type = "Type",
}

interface CancellationReasonOption {
    Type?: string;
    Categories?: CancellationReasonCategory[];
}

interface CancellationReasonCategory {
    Category?: string;
    Subcategories?: CancellationReasonSubcategory[];
}

interface CancellationReasonSubcategory {
    Subcategory?: string;
    ReturnDateField?: boolean;
    AddRestrictionTagField?: boolean;
}

type CompletionStatusType = keyof typeof CompletionStatus;

type Props = {
  navigateTarget: string;
  cancellationReasonState: CancellationReasonState;
  cancellationReasonDispatch: React.Dispatch<CancellationReasonAction>;
};

// create data structure using values stored in state pass into generate next dropdown
export const generateNextDropdownData = (
    level: CompletionStatusType,
    dataStructure: Record<CompletionStatusType, string>,
    result: CancellationReasonOption[]
): CancellationReasonCategory[] | CancellationReasonSubcategory[] => {
    // Logic to generate the next dropdown data based on the selected values
    switch (level) {
        case CompletionStatus.Type:
            return result.find((item) => item.Type === dataStructure[level])?.Categories || [];
        case CompletionStatus.Category:
            return (
                result
                .find((item) => item.Type === dataStructure[CompletionStatus.Type])
                ?.Categories?.find((category) => category.Category === dataStructure[level])
                ?.Subcategories || []
            );
      default:
          return [];
    }
};

const result = cancellationReasons;

export default function CancellationReason({
  navigateTarget,
  cancellationReasonState,
  cancellationReasonDispatch
}: Props) {
    const [selectedType, setSelectedType] = useState<string>(cancellationReasonState.cancellationType);
    const [selectedCategory, setSelectedCategory] = useState<string>(cancellationReasonState.cancellationCategory);
    const [selectedSubcategory, setSelectedSubcategory] = useState<string>(cancellationReasonState.cancellationSubCategory);
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
    const navigate = useNavigate();

    // Initialize local state from reducer state on mount
    useEffect(() => {
        setSelectedType(cancellationReasonState.cancellationType);
        setSelectedCategory(cancellationReasonState.cancellationCategory);
        setSelectedSubcategory(cancellationReasonState.cancellationSubCategory);
    }, []);

    const handleMainDropdownChange = (level: CompletionStatusType, value: string) => {
        // Update the selected value based on the dropdown level
        switch (level) {
            case CompletionStatus.Type:
                setSelectedType(value);
                cancellationReasonDispatch({ type: "SET_CANCELLATION_TYPE", payload: value });
                cancellationReasonDispatch({ type: "SET_CANCELLATION_CATEGORY", payload: '' });
                cancellationReasonDispatch({ type: "SET_CANCELLATION_SUBCATEGORY", payload: '' });
                setSelectedCategory('');
                setSelectedSubcategory('');
                break;
            case CompletionStatus.Category:
                setSelectedCategory(value);
                cancellationReasonDispatch({ type: "SET_CANCELLATION_CATEGORY", payload: value });
                cancellationReasonDispatch({ type: "SET_CANCELLATION_SUBCATEGORY", payload: '' });
                setSelectedSubcategory('');
                break;
            case CompletionStatus.Subcategory:
                setSelectedSubcategory(value);
                cancellationReasonDispatch({ type: "SET_CANCELLATION_SUBCATEGORY", payload: value });
                break;
            default:
                break;
        }
    };

    const allCancellationReasons: Record<CompletionStatus, string> = {
        [CompletionStatus.Type]: selectedType,
        [CompletionStatus.Category]: selectedCategory,
        [CompletionStatus.Subcategory]: selectedSubcategory,
    };

    const dropDownListStyles: CSSProperties = {
        height: '5vh',
        border: '1px solid #C4C4C4',
        borderRadius: '10px',
        minWidth: '100%',
    };

    const get_dropdown_title_for_level = (level: string) => {
        switch (level) {
            case CompletionStatus.Type:
                return 'Cancellation type';
            case CompletionStatus.Category:
                return 'Cancellation reason - outcome';
            case CompletionStatus.Subcategory:
                return 'Cancellation reason - reason';
            default:
                return 'Select';
        }
    }

    const renderDropdown = (level: CompletionStatusType, options: any) => (
        options && options.length > 0 && (
            <div key={level}>
                <Label text={get_dropdown_title_for_level(level)} requiredFieldInd={true} />
                <select
                    style={dropDownListStyles}
                    name={'renderedDropdown'}
                    data-testid={'drop-down-menu-testid-' + level}
                    onChange={(e) => handleMainDropdownChange(level, e.target.value)}
                    value={allCancellationReasons[level]}
                    required={true}
                >
                    <option value="">{`- please select -`}</option>
                    {options.map((item: any) => (
                        <option key={item[level]}
                                value={item[level]}
                        >
                            {item[level]}
                        </option>
                    ))}
                </select>
                {/*If the level is subcategory, check if any extra fields needed to be rendered*/}
                {level === 'Subcategory'
                    && allCancellationReasons["Subcategory"]
                    && options.some((option: {
                        Subcategory: any;
                        ReturnDateField: any;
                        AddRestrictionTagField: any;
                    }) => option.Subcategory === allCancellationReasons[CompletionStatus.Subcategory]) && (
                        <div style={divStyles}>

                            {/*Return date field*/}
                            {options.find((option: {
                                    Subcategory: any;
                                    ReturnDateField: any;
                                }) => option.Subcategory === allCancellationReasons[CompletionStatus.Subcategory]).ReturnDateField === true &&
                                <>
                                    <Label text={'Return Date'}/>
                                    <DatePicker
                                        name='contractEndDate'
                                        data-testid="contractEndDate"
                                        onChange={onReturnDateChange}
                                        defaultVal={cancellationReasonState.customerReturnDate}
                                    />
                                </>
                            }

                            {/*Add restriction tag field*/}
                            {options.find((option: {
                                    Subcategory: any;
                                    AddRestrictionTagField: any;
                                }) => option.Subcategory === allCancellationReasons[CompletionStatus.Subcategory]).AddRestrictionTagField === true &&
                                <>
                                    <Label text={'Add restriction tag to account?'}/>
                                    <select
                                        style={dropDownListStyles}
                                        name={'restrictionTag'}
                                        data-testid={'restriction-tag-testid'}
                                        onChange={handleRestrictionTagChange}
                                        value={cancellationReasonState.cancellationAddRestrictionToAccount}
                                    >
                                        {dropDownRestrictionTagOptions.map((item: any) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            }

                        </div>
                    )}
            </div>
        )
    );

    const checkIsButtonDisabled = () => {
        if (selectedCategory !== '' && selectedType !== '') {
            return false
        } else if (selectedCategory !== '') {
            return false
        } else {
            return true
        }
    }

    useEffect(() => {
        setIsButtonDisabled(checkIsButtonDisabled())
    }, [selectedCategory, selectedType])

    const handleAdditionalInfoComments = (e: React.ChangeEvent<HTMLInputElement>) => {
        cancellationReasonDispatch({ type: "SET_ADDITIONAL_INFO", payload: e.target.value });
    };

    const onReturnDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        cancellationReasonDispatch({ type: "SET_CUSTOMER_RETURN_DATE", payload: e.target.value });
    }

    const dropDownRestrictionTagOptions = [
        {label: 'Yes', value: 'Yes'},
        {label: 'No', value: 'No'}
    ];

    const handleRestrictionTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        cancellationReasonDispatch({ type: "SET_ADD_RESTRICTION_TO_ACCOUNT", payload: e.target.value });
    };

    const dropDownVehicleRemarketableOptions = [
        {label: 'Yes', value: 'true'},
        {label: 'No', value: 'false'}
    ];

    const handleVehicleRemarketableChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const isRemarketable = e.target.value === 'true';
        cancellationReasonDispatch({ type: "SET_VEHICLE_REMARKETABLE", payload: isRemarketable });
    };

    useEffect(() => {
        if (selectedType === 'Involuntary (Splend Choice)') {
            cancellationReasonDispatch({ type: "SET_ADD_RESTRICTION_TO_ACCOUNT", payload: 'Yes' });
        } else {
            cancellationReasonDispatch({ type: "SET_ADD_RESTRICTION_TO_ACCOUNT", payload: '' });
        }
    }, [selectedSubcategory]);

    const divStyles: CSSProperties = {
        margin: '2.5vh 0vw',
    };

    const handleNext = () => {
      navigate(navigateTarget);
    };

    const is_internal_fo2fo_transfer: boolean = selectedType === 'Internal Vehicle Transfers' && selectedCategory === 'Flexi Own to Flexi Own';
    const is_fo_completion: boolean = selectedType === 'Flexi Own Completion';

    // Vehicle Remarketable dropdown is not applicable for Internal Vehicle Transfers with FO to FO and FO completion
    const isRemarketableApplicable: boolean = !(is_internal_fo2fo_transfer || is_fo_completion);

    useEffect(() => {
        // When the reason options change, the dropdown will be hidden/unhidden.
        // So we do not need to change the currently selected dropdown option. But we do want to make the value null
        // or set (defaults to true)
        if (!isRemarketableApplicable){
            cancellationReasonDispatch({ type: "SET_VEHICLE_REMARKETABLE", payload: null });
        } else {
            cancellationReasonDispatch({ type: "SET_VEHICLE_REMARKETABLE", payload: true})
        }
    }, [isRemarketableApplicable])

    return (<>
        <NavBar pageTitle="Cancellation - Reason" />
        <div style={{ margin: '2.5vh 2.5vw' }}>
          <div style={divStyles}>
              <p>Please record a <strong>reason for the cancellation</strong>, providing as much detail as possible.</p>
          </div>
          <div style={divStyles}>
              {/*Render type dropdown*/}
              {renderDropdown(CompletionStatus.Type, result)}
          </div>

          <div style={divStyles}>
              {/*Render category dropdown*/}
              {selectedType &&
                  renderDropdown(
                      CompletionStatus.Category,
                      generateNextDropdownData(CompletionStatus.Type, allCancellationReasons, result)
                  )}
          </div>
          <div style={divStyles}>
              {/*Render subcategory dropdown and additional fields if required*/}
              {selectedCategory &&
                  renderDropdown(
                      CompletionStatus.Subcategory,
                      generateNextDropdownData(CompletionStatus.Category, allCancellationReasons, result)
                  )}
          </div>

          {/*Vehicle Remarketable dropdown - hidden for Internal Vehicle Transfers with FO to FO or FO completion*/}
          {isRemarketableApplicable && (
              <div style={divStyles}>
                  <Label text={'Vehicle Re-marketable'}/>
                  <select
                      style={dropDownListStyles}
                      name='vehicleRemarketable'
                      data-testid='vehicle-remarketable-testid'
                      onChange={handleVehicleRemarketableChange}
                      value={cancellationReasonState.vehicleRemarketable != null ? String(cancellationReasonState.vehicleRemarketable) : ''}
                      required={true}
                  >
                      {dropDownVehicleRemarketableOptions.map((item: any) => (
                          <option key={item.value} value={item.value}>
                              {item.label}
                          </option>
                      ))}
                  </select>
              </div>
          )}

          {/*Additional comments field is present for all types / categories*/}
          <div style={divStyles}>
              <Label text={'Additional Comments'}/>
              <input
                  style={dropDownListStyles}
                  name={'additionalComments'}
                  data-testid={'additional-comments-testid'}
                  onChange={handleAdditionalInfoComments}
                  value={cancellationReasonState.cancellationAdditionalInfo}
              />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5vh 0', marginTop: '2.5vh' }}>
            <Button
              label="Next"
              onClickFunc={handleNext}
              disabled={isButtonDisabled}
            />
          </div>
        </div>
    </>);
};
